import { useState, useCallback } from "react";
import { mutate } from "swr";
import {
  usePosContext,
  POS_CUSTOMER_INFO_KEY,
} from "../context/pos-context";
import { getStorage, setStorage } from "@/hooks/use-local-storage";
import { useUpdateCustomer } from "@/services/customer-service";

const CUSTOMERS_BY_TELEGRAM_KEY_PREFIX = "/customers/by-telegram/";

export function useCustomerInfoCheckout() {
  const {
    handleCheckout,
    telegramId,
    customerByTelegram,
    isCustomerByTelegramLoading,
  } = usePosContext();
  const { executeAsync: updateCustomerAsync } = useUpdateCustomer();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const customerName = customerByTelegram?.name?.trim() ?? "";
  const customerPhone = customerByTelegram?.phone?.trim() ?? "";
  const hasCustomerFromTelegram =
    Boolean(telegramId) && !isCustomerByTelegramLoading && customerByTelegram;
  const hasNameAndPhoneFromTelegram =
    hasCustomerFromTelegram && Boolean(customerName && customerPhone);
  const hasNameOnlyFromTelegram =
    hasCustomerFromTelegram && Boolean(customerName) && !customerPhone;

  const handlePayClick = useCallback(() => {
    const storedInfo = getStorage(POS_CUSTOMER_INFO_KEY) as
      | { customerName?: string; customerPhone?: string; customerAddress?: string }
      | null;
    const storedName = storedInfo?.customerName?.trim() ?? "";
    const storedPhone = storedInfo?.customerPhone?.trim() ?? "";
    const hasStoredInfo = Boolean(storedName && storedPhone);

    if (hasNameAndPhoneFromTelegram) {
      setStorage(POS_CUSTOMER_INFO_KEY, {
        customerName: customerName,
        customerPhone: customerPhone,
        customerAddress: customerByTelegram?.address ?? "",
      });
      handleCheckout({ customerId: customerByTelegram!.id });
      return;
    }

    if (hasNameOnlyFromTelegram) {
      setIsDialogOpen(true);
      return;
    }

    if (hasStoredInfo) {
      handleCheckout();
      return;
    }

    setIsDialogOpen(true);
  }, [
    handleCheckout,
    hasNameAndPhoneFromTelegram,
    hasNameOnlyFromTelegram,
    customerName,
    customerPhone,
    customerByTelegram,
  ]);

  const handleCustomerInfoSubmit = useCallback(
    async (name: string, phone: string, address?: string) => {
      const trimmedName = name.trim();
      const trimmedPhone = phone.trim();
      const trimmedAddress = address?.trim() ?? "";
      if (!trimmedName || !trimmedPhone) return;

      if (hasNameOnlyFromTelegram && customerByTelegram && telegramId) {
        try {
          await updateCustomerAsync({
            id: customerByTelegram.id,
            data: {
              name: customerByTelegram.name,
              phone: trimmedPhone,
              address: trimmedAddress || undefined,
              saleChannel: "POS",
              notTransfer: false,
            },
          });
          await mutate(`${CUSTOMERS_BY_TELEGRAM_KEY_PREFIX}${telegramId}`);
        } catch {
          return;
        }
      }

      setStorage(POS_CUSTOMER_INFO_KEY, {
        customerName: trimmedName,
        customerPhone: trimmedPhone,
        customerAddress: trimmedAddress,
      });
      setIsDialogOpen(false);
      handleCheckout(
        customerByTelegram ? { customerId: customerByTelegram.id } : undefined
      );
    },
    [
      handleCheckout,
      hasNameOnlyFromTelegram,
      customerByTelegram,
      telegramId,
      updateCustomerAsync,
    ]
  );

  const dialogMode: "both" | "phoneOnly" =
    hasNameOnlyFromTelegram ? "phoneOnly" : "both";
  const dialogInitialName = hasNameOnlyFromTelegram ? customerName : undefined;
  const dialogInitialPhone = hasNameOnlyFromTelegram ? "" : undefined;
  const dialogInitialAddress =
    hasNameOnlyFromTelegram ? (customerByTelegram?.address?.trim() ?? "") : undefined;

  return {
    isDialogOpen,
    setIsDialogOpen,
    handlePayClick,
    handleCustomerInfoSubmit,
    dialogMode,
    dialogInitialName,
    dialogInitialPhone,
    dialogInitialAddress,
  };
}
